import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { API_BASE_URL } from "../../utils/constants";
import { readErrorMessage } from "../../utils/apiError";
import DocumentFormats from "./DocumentFormats";

// Everything here is printed on the case documents (appointment letter, notices, roznamas, Sec 17 order, award)
const BANK_FIELDS = [
  { key: "client_name", label: "Bank name *", hint: "Printed as the Claimant on every document" },
  { key: "Add1", label: "Branch / corporate office address", rows: 2 },
  { key: "Add2", label: "Registered office address", rows: 2 },
  {
    key: "claimantDescription",
    label: "Claimant description (Award)",
    rows: 5,
    hint: 'Award paragraph 2, printed after the bank name: "The claimant <bank name> …". E.g. what kind of institution it is, its registered and corporate offices, and its business.',
  },
  { key: "claimantAdvocate", label: "Claimant's advocate", hint: 'Name only, e.g. "Rupali Gaikwad": documents print "Adv. Rupali Gaikwad"' },
  { key: "claimantAdvocateFirm", label: "Claimant's advocate firm", hint: 'E.g. "Acumen Lawyers Firm": awards print "i/by Acumen Lawyers Firm", roznamas "Advocate on behalf of Acumen Lawyers Firm"' },
  { key: "Authority_name", label: "Authorised signatory of the bank", hint: 'As it should print, e.g. "Mr. Ashok Wakchaure"' },
];

const BankFormModal = ({ show, bank, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // Word formats: chosen uploads and why the bank can't be saved yet (all are required for a new bank)
  const [formats, setFormats] = useState({ selection: {}, blocking: "Loading the document list…" });

  useEffect(() => {
    if (!show) return;
    setError("");
    setForm(Object.fromEntries(BANK_FIELDS.map((f) => [f.key, bank?.[f.key] || ""])));
  }, [show, bank]);

  const handleSave = async () => {
    setError("");
    if (!form.client_name?.trim()) {
      setError("Bank name is required");
      return;
    }
    if (formats.blocking) {
      setError(formats.blocking);
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/Client${bank ? `/${bank.Client_id}` : ""}`, {
        method: bank ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, templates: formats.selection }),
      });
      if (!res.ok) throw new Error(await readErrorMessage(res));
      onSaved(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" backdrop="static" scrollable>
      <Modal.Header closeButton closeVariant="white" className="customModal">
        <Modal.Title className="mx-auto" style={{ fontSize: "16px" }}>
          {bank ? "Edit Bank" : "Add Bank"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: "12px" }}>
        {error && <Alert variant="danger" style={{ whiteSpace: "pre-line" }}>{error}</Alert>}
        <div className="row g-3">
          {BANK_FIELDS.map((f) => (
            <div key={f.key} className="col-12">
              <Form.Label className="mb-1">{f.label}</Form.Label>
              <Form.Control
                as={f.rows ? "textarea" : "input"}
                rows={f.rows}
                value={form[f.key] || ""}
                onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                style={{ fontSize: "12px" }}
              />
              {f.hint && <Form.Text className="text-muted">{f.hint}</Form.Text>}
            </div>
          ))}
        </div>
        <hr />
        {show && (
          <DocumentFormats
            level="bank"
            owner={bank ? { clientId: bank.Client_id, productId: null } : null}
            onChange={setFormats}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        {formats.blocking && (
          <div className="w-100 text-danger" style={{ fontSize: "12px" }}>
            Can't save yet: {formats.blocking}
          </div>
        )}
        <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <button
          className="custBtn"
          style={{ fontSize: "12px" }}
          onClick={handleSave}
          disabled={saving || Boolean(formats.blocking)}
          title={formats.blocking || undefined}
        >
          {saving ? "Saving..." : "Save Bank"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default BankFormModal;
